import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import Layout from '../../../layouts/MainLayout';
import PageHeader from '../../../components/PageHeader';
import {
  getAgents,
  getCompetitors,
  getReferenceUrlScopes,
} from '../assistantsService';
import agentNotConfiguredIllustration from '../../../assets/agent-not-configured.png';
const DEFAULT_CONFIG_SPEC = {
  description: 'Choose one competitor website or provide one custom URL.',
  supportsCompetitorList: true,
  supportsCustomUrl: true,
  supportsStateScope: false,
  stateScopeType: undefined,
  competitorSelectionMode: 'single',
};

const getAgentConfigSpec = (agent) => {
  const slug = (agent?.slug || '').trim().toLowerCase();
  const bySlug = {
    market_intel_targeted: DEFAULT_CONFIG_SPEC,
    market_intel_global: DEFAULT_CONFIG_SPEC,
    jobs_agent: {
      ...DEFAULT_CONFIG_SPEC,
      description: 'Choose one competitor to analyze recent job postings and hiring signals.',
    },
    state_laws_regulation_deep_search: {
      description: 'Choose one jurisdiction to run state laws/regulation deep search.',
      supportsCompetitorList: false,
      supportsCustomUrl: false,
      supportsStateScope: true,
      stateScopeType: 'state',
      competitorSelectionMode: 'single',
    },
    doi_researcher_deep_search: {
      description: 'Choose one jurisdiction to run DOI researcher deep search.',
      supportsCompetitorList: false,
      supportsCustomUrl: false,
      supportsStateScope: true,
      stateScopeType: 'state',
      competitorSelectionMode: 'single',
    },
  };

  if (bySlug[slug]) return bySlug[slug];

  if ((agent?.agent_group || '').toLowerCase() === 'market_intelligence') {
    const name = (agent?.name || '').toLowerCase();
    if (name.includes('deep researcher competitor') || name.includes('rival web researcher')) {
      return DEFAULT_CONFIG_SPEC;
    }
    return {
      description: 'Choose one competitor website or provide one custom URL.',
      supportsCompetitorList: false,
      supportsCustomUrl: false,
      supportsStateScope: false,
      competitorSelectionMode: 'single',
    };
  }

  return {
    description: 'This assistant has no additional configuration yet.',
    supportsCompetitorList: false,
    supportsCustomUrl: false,
    supportsStateScope: false,
    competitorSelectionMode: 'single',
  };
};

const isAgentConfigurable = (agent) => {
  const spec = getAgentConfigSpec(agent);
  return spec.supportsCompetitorList || spec.supportsCustomUrl || spec.supportsStateScope;
};

const sortAgentsConfigurableFirst = (agents) => [...agents].sort((a, b) => {
  const aConfigurable = isAgentConfigurable(a);
  const bConfigurable = isAgentConfigurable(b);
  if (aConfigurable !== bConfigurable) return aConfigurable ? -1 : 1;
  return (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
});

const normalizeCompetitor = (entry) => {
  if (typeof entry === 'string') {
    const name = entry.trim();
    return name ? { name, type: 'Other', key: `${name}::Other` } : null;
  }
  const name = (entry?.name || '').trim();
  if (!name) return null;
  const type = (entry?.type || entry?.competitor_type || 'Other').trim() || 'Other';
  return { name, type, key: `${name}::${type}` };
};

const buildGroupedCompetitors = (competitors) => {
  const groups = new Map();
  competitors.forEach((competitor) => {
    const bucket = groups.get(competitor.type) || [];
    bucket.push(competitor);
    groups.set(competitor.type, bucket);
  });

  const rank = (type) => {
    const normalized = type.trim().toLowerCase();
    if (normalized === 'health') return 0;
    if (normalized === 'life and annuities') return 1;
    if (normalized === 'property and casualty') return 2;
    return 99;
  };

  return [...groups.entries()]
    .sort((a, b) => rank(a[0]) - rank(b[0]) || a[0].localeCompare(b[0]))
    .map(([type, items]) => ({
      type,
      displayType: type,
      items: [...items].sort((a, b) => a.name.localeCompare(b.name)),
    }));
};

const mapJurisdictionScopeOption = (entry) => {
  const scopeKey = (entry?.scope_key || '').trim();
  if (!scopeKey) return null;
  const jurisdictionName = (entry?.jurisdiction_name || '').trim() || scopeKey;
  return { scopeKey, jurisdictionName };
};

const getJurisdictionAbbreviation = (scopeKey) => {
  const normalized = (scopeKey || '').trim();
  if (!normalized) return '';
  const tokens = normalized.split(/[^a-zA-Z0-9]+/).filter(Boolean);
  return (tokens.length ? tokens[tokens.length - 1] : normalized).toUpperCase();
};

const isValidHttpUrl = (value) => {
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
};

const resolveResearcherRoute = (agent) => {
  const name = (agent?.name || '').trim().toLowerCase();
  const slug = (agent?.slug || '').trim().toLowerCase();
  if (slug === 'state_laws_regulation_deep_search') return '/state-laws-regulation-deep-search';
  if (slug === 'doi_researcher_deep_search') return '/doi-researcher-deep-search';
  if (slug === 'jobs_agent') return '/jobs-agent';
  // Match on slug: the DB name is "Competitor Deep Researcher", so the older
  // name.includes('deep researcher competitor') check never matched and this
  // agent fell through to /competitor-web-researcher (losing its identity on refresh).
  if (slug === 'market_intel_global') return '/deep-researcher-competitor';
  if (name.includes('deep researcher competitor')) return '/deep-researcher-competitor';
  return '/competitor-web-researcher';
};

const isMarketIntelligenceAgent = (agent) =>
  (agent?.agent_group || '').trim().toLowerCase() === 'market_intelligence';

const formatAgentGroupLabel = (agentGroup) => {
  const raw = (agentGroup || '').trim();
  if (!raw) return 'Compliance';
  return raw
    .split('_')
    .filter(Boolean)
    .map((word, index) => {
      const lower = word.toLowerCase();
      return index === 0
        ? `${lower.charAt(0).toUpperCase()}${lower.slice(1)}`
        : lower;
    })
    .join(' ');
};

const agentTypeBadgeMeta = (agent) => {
  const isMarket = isMarketIntelligenceAgent(agent);
  const label = isMarket ? 'Market intelligence' : formatAgentGroupLabel(agent?.agent_group);
  return {
    label,
    variant: isMarket ? 'market' : 'compliance',
    ariaLabel: `${label} assistant type`,
  };
};

const Assistants = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showConfigureModal, setShowConfigureModal] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [competitors, setCompetitors] = useState([]);
  const [competitorsLoading, setCompetitorsLoading] = useState(false);
  const [competitorsError, setCompetitorsError] = useState(null);
  const [jurisdictionOptions, setJurisdictionOptions] = useState([]);
  const [jurisdictionLoading, setJurisdictionLoading] = useState(false);
  const [jurisdictionError, setJurisdictionError] = useState(null);
  const [selectedJurisdictionScopeKey, setSelectedJurisdictionScopeKey] = useState('');
  const [configureMode, setConfigureMode] = useState('competitor');
  const [selectedCompetitors, setSelectedCompetitors] = useState([]);
  const [customUrl, setCustomUrl] = useState('');
  const [configureValidationError, setConfigureValidationError] = useState(null);
  const [activeConfigSpec, setActiveConfigSpec] = useState(DEFAULT_CONFIG_SPEC);
  const [configuredPayloads, setConfiguredPayloads] = useState({});

  useEffect(() => {
    if (!showConfigureModal) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [showConfigureModal]);

  useEffect(() => {
    let cancelled = false;
    const loadAgents = async () => {
      try {
        const list = await getAgents();
        if (!cancelled) setAgents(list.filter(isMarketIntelligenceAgent));
      } catch (err) {
        console.error('Failed to parse agents', err);
        if (!cancelled) setError('Failed to load agents. Please try again later.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    loadAgents();
    return () => {
      cancelled = true;
    };
  }, []);

  const sortedAgents = useMemo(() => sortAgentsConfigurableFirst(agents), [agents]);

  const groupedCompetitorOptions = useMemo(() => buildGroupedCompetitors(competitors), [competitors]);

  const competitorColumns = useMemo(() => {
    const match = (key) => groupedCompetitorOptions
      .filter((group) => group.type.trim().toLowerCase() === key.toLowerCase())
      .flatMap((group) => group.items);
    return [
      { key: 'life and annuities', label: 'Life and Annuities', items: match('life and annuities') },
      { key: 'health', label: 'Health', items: match('health') },
      { key: 'property and casualty', label: 'Property and Casualty', items: match('property and casualty') },
    ];
  }, [groupedCompetitorOptions]);

  const hasCompetitorsInColumns = competitorColumns.some((column) => column.items.length > 0);
  const hasAnyConfigOption = activeConfigSpec.supportsCompetitorList || activeConfigSpec.supportsCustomUrl || activeConfigSpec.supportsStateScope;
  const configureDialogTitle = activeConfigSpec.supportsStateScope ? 'Choose State' : 'Choose competitor';
  const configureDialogLayoutClass = !hasAnyConfigOption
    ? 'configure-dialog--unconfigured'
    : activeConfigSpec.supportsStateScope
      ? 'configure-dialog--jurisdiction'
      : (activeConfigSpec.supportsCompetitorList && configureMode === 'competitor'
        ? 'configure-dialog--competitor'
        : '');
  const shouldShowModeSwitch = activeConfigSpec.supportsCompetitorList && activeConfigSpec.supportsCustomUrl;

  const loadCompetitors = async () => {
    setCompetitorsLoading(true);
    setCompetitorsError(null);
    try {
      const list = await getCompetitors();
      const normalized = list.map(normalizeCompetitor).filter(Boolean);
      const uniqueByKey = new Map();
      normalized.forEach((competitor) => {
        if (!uniqueByKey.has(competitor.key)) uniqueByKey.set(competitor.key, competitor);
      });
      setCompetitors([...uniqueByKey.values()]);
    } catch (err) {
      console.error('Failed to load competitors', err);
      setCompetitorsError('Failed to load competitor websites. Please try again.');
    } finally {
      setCompetitorsLoading(false);
    }
  };

  const loadJurisdictionScopes = async (agentSlug) => {
    if (!agentSlug) return;
    setJurisdictionLoading(true);
    setJurisdictionError(null);
    setJurisdictionOptions([]);
    try {
      const scopes = await getReferenceUrlScopes({ agentSlug, scopeType: 'state' });
      const options = scopes
        .map(mapJurisdictionScopeOption)
        .filter(Boolean)
        .sort((a, b) => a.jurisdictionName.localeCompare(b.jurisdictionName));
      setJurisdictionOptions(options);
      setSelectedJurisdictionScopeKey((current) => (
        current && !options.some((option) => option.scopeKey === current) ? '' : current
      ));
    } catch (err) {
      console.error('Failed to load jurisdictions', err);
      setJurisdictionError('Failed to load jurisdictions. Please try again.');
    } finally {
      setJurisdictionLoading(false);
    }
  };

  const openConfigure = (agent) => {
    const spec = getAgentConfigSpec(agent);
    const saved = configuredPayloads[agent.slug];
    setSelectedAgent(agent);
    setShowConfigureModal(true);
    setConfigureValidationError(null);
    setActiveConfigSpec(spec);
    setSelectedJurisdictionScopeKey('');

    if (saved?.scope_type && spec.supportsStateScope) {
      setSelectedJurisdictionScopeKey(saved.scope_key || '');
      setConfigureMode('competitor');
      setSelectedCompetitors([]);
      setCustomUrl('');
    } else if (saved?.is_custom_url && spec.supportsCustomUrl) {
      setConfigureMode('custom');
      setCustomUrl(saved.user_preferred_urls?.[0] || '');
      setSelectedCompetitors([]);
    } else if (saved?.competitors?.length && spec.supportsCompetitorList) {
      setConfigureMode('competitor');
      setSelectedCompetitors(spec.competitorSelectionMode === 'multi' ? [...saved.competitors] : [saved.competitors[0]].filter(Boolean));
      setCustomUrl('');
    } else if (spec.supportsCompetitorList) {
      setConfigureMode('competitor');
      setSelectedCompetitors([]);
      setCustomUrl('');
    } else if (spec.supportsCustomUrl) {
      setConfigureMode('custom');
      setSelectedCompetitors([]);
      setCustomUrl('');
    } else {
      setConfigureMode('competitor');
      setSelectedCompetitors([]);
      setCustomUrl('');
    }

    if (spec.supportsCompetitorList && !competitors.length && !competitorsLoading) loadCompetitors();
    if (spec.supportsStateScope && !jurisdictionLoading) loadJurisdictionScopes(agent.slug);
  };

  const closeConfigure = () => {
    setShowConfigureModal(false);
    setConfigureValidationError(null);
  };

  const onConfigureModeChange = (mode) => {
    setConfigureMode(mode);
    setConfigureValidationError(null);
    if (mode === 'competitor') {
      setCustomUrl('');
    } else {
      setSelectedCompetitors([]);
    }
  };

  const isCompetitorSelected = (competitor) => selectedCompetitors.includes(competitor);
  const isJurisdictionSelected = (scopeKey) => selectedJurisdictionScopeKey === scopeKey;

  const toggleCompetitor = (competitor, checked) => {
    if (activeConfigSpec.competitorSelectionMode === 'multi') {
      setSelectedCompetitors((current) => (
        checked
          ? (current.includes(competitor) ? current : [...current, competitor])
          : current.filter((name) => name !== competitor)
      ));
      return;
    }
    setSelectedCompetitors(checked ? [competitor] : []);
  };

  const isAddDisabled = () => {
    if (!hasAnyConfigOption) return true;
    if (activeConfigSpec.supportsStateScope) return !selectedJurisdictionScopeKey;
    if (configureMode === 'competitor' && activeConfigSpec.supportsCompetitorList) return !selectedCompetitors.length;
    if (configureMode === 'custom' && activeConfigSpec.supportsCustomUrl) return !customUrl.trim();
    return false;
  };

  const navigateToResearcher = (payload) => {
    navigate(resolveResearcherRoute(selectedAgent), { state: { assistantPayload: payload } });
  };

  const saveConfiguration = () => {
    const agent = selectedAgent;
    if (!agent) return;

    if (!activeConfigSpec.supportsCompetitorList && !activeConfigSpec.supportsCustomUrl && !activeConfigSpec.supportsStateScope) {
      const payload = {
        competitors: [],
        is_custom_url: false,
        question: '',
        previous_response_id: null,
        agent_slug: agent.slug,
      };
      setConfiguredPayloads((current) => ({ ...current, [agent.slug]: payload }));
      navigateToResearcher(payload);
      closeConfigure();
      return;
    }

    if (activeConfigSpec.supportsStateScope) {
      if (!selectedJurisdictionScopeKey) {
        setConfigureValidationError('Please select a jurisdiction.');
        return;
      }
      const selected = jurisdictionOptions.find((option) => option.scopeKey === selectedJurisdictionScopeKey);
      const payload = {
        competitors: [],
        is_custom_url: false,
        question: '',
        previous_response_id: null,
        agent_slug: agent.slug,
        scope_type: activeConfigSpec.stateScopeType || 'state',
        scope_key: selectedJurisdictionScopeKey,
        jurisdiction_name: selected?.jurisdictionName || selectedJurisdictionScopeKey,
      };
      setConfiguredPayloads((current) => ({ ...current, [agent.slug]: payload }));
      navigateToResearcher(payload);
      closeConfigure();
      return;
    }

    if (configureMode === 'competitor') {
      if (!activeConfigSpec.supportsCompetitorList) {
        setConfigureValidationError('This assistant does not support competitor selection.');
        return;
      }
      if (!selectedCompetitors.length) {
        setConfigureValidationError(activeConfigSpec.competitorSelectionMode === 'multi'
          ? 'Please select at least one competitor website.'
          : 'Please select one competitor website.');
        return;
      }
      const payload = {
        competitors: [...selectedCompetitors],
        is_custom_url: false,
        question: '',
        previous_response_id: null,
        agent_slug: agent.slug,
      };
      setConfiguredPayloads((current) => ({ ...current, [agent.slug]: payload }));
      navigateToResearcher(payload);
      closeConfigure();
      return;
    }

    if (!activeConfigSpec.supportsCustomUrl) {
      setConfigureValidationError('This assistant does not support custom URL input.');
      return;
    }

    const url = customUrl.trim();
    if (!isValidHttpUrl(url)) {
      setConfigureValidationError('Please enter a valid http(s) URL.');
      return;
    }

    const payload = {
      competitors: [],
      is_custom_url: true,
      user_preferred_urls: [url],
      question: '',
      previous_response_id: null,
      agent_slug: agent.slug,
    };
    setConfiguredPayloads((current) => ({ ...current, [agent.slug]: payload }));
    navigateToResearcher(payload);
    closeConfigure();
  };

  return (
    <Layout>
      <PageHeader title="Pre-built Assistants" />
      <div key={location.key} className="assistants-page">
        <div className="assistants-surface">
          <div className="assistants-header">
            <p className="assistants-sub">Select an assistant and configure it to get started</p>
          </div>

          {isLoading && <p className="assistants-status">Loading assistants...</p>}
          {!isLoading && error && <p className="assistants-status error">{error}</p>}
          {!isLoading && !error && !agents.length && <p className="assistants-status">No assistants available for this client.</p>}

          {!isLoading && !error && agents.length > 0 && (
            <div className="agents-grid">
              {sortedAgents.map((agent) => {
                const badge = agentTypeBadgeMeta(agent);
                return (
                  <div className="agent-card" key={agent.id || agent.slug}>
                    <div className="agent-card-header">
                      <span
                        className={`agent-type-badge ${badge.variant}`}
                        aria-label={badge.ariaLabel}
                        title={badge.label}
                      >
                        {badge.label}
                      </span>
                      <div className="title-wrap">
                        {/* <div className="title-bar" /> */}
                        <h2 className="title">{agent.name}</h2>
                      </div>
                    </div>
                    <div className="card-separator" />
                    <p className="agent-card-desc">{agent.description || 'No description available.'}</p>
                    <div className="agent-card-footer">
                      <button className="configure-btn" type="button" onClick={() => openConfigure(agent)}>
                        <span className="pi">+</span> Configure
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

        {showConfigureModal && createPortal(
          <div className="configure-dialog-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) closeConfigure(); }}>
            <div className={`configure-dialog ${configureDialogLayoutClass}`.trim()} role="dialog" aria-modal="true" aria-label={configureDialogTitle}>
              <div className="p-dialog-header">
                <span className="p-dialog-title">{configureDialogTitle}</span>
                <button type="button" className="drawer-close filters-drawer-close" onClick={closeConfigure} aria-label="Close">×</button>
              </div>
              <div className="p-dialog-content">
                {shouldShowModeSwitch && (
                  <div className="config-mode-row">
                    <label className="mode-option">
                      <input type="radio" name="configure-mode" checked={configureMode === 'competitor'} onChange={() => onConfigureModeChange('competitor')} />
                      <span>From competitor list</span>
                    </label>
                    <label className="mode-option">
                      <input type="radio" name="configure-mode" checked={configureMode === 'custom'} onChange={() => onConfigureModeChange('custom')} />
                      <span>Custom URL</span>
                    </label>
                  </div>
                )}

                {activeConfigSpec.supportsCompetitorList && configureMode === 'competitor' && (
                  <div className="competitor-picker">
                    {competitorsLoading && <p className="assistants-status">Loading competitor websites...</p>}
                    {!competitorsLoading && competitorsError && <p className="assistants-status error">{competitorsError}</p>}
                    {!competitorsLoading && !competitorsError && (
                      <>
                        <div className="competitor-columns">
                          {competitorColumns.map((column) => (
                            <section className="competitor-column" key={column.key}>
                              <div className="competitor-group-heading">{column.label}</div>
                              <div className={`competitor-list ${activeConfigSpec.competitorSelectionMode === 'single' ? 'single-select' : ''}`}>
                                {column.items.map((competitor) => (
                                  <label className={`competitor-item ${isCompetitorSelected(competitor.name) ? 'selected' : ''}`} key={competitor.key}>
                                    <input
                                      type={activeConfigSpec.competitorSelectionMode === 'single' ? 'radio' : 'checkbox'}
                                      name={activeConfigSpec.competitorSelectionMode === 'single' ? 'selected-competitor-single' : `selected-competitor-${competitor.key}`}
                                      value={competitor.name}
                                      checked={isCompetitorSelected(competitor.name)}
                                      onChange={(event) => toggleCompetitor(competitor.name, event.target.checked)}
                                    />
                                    <span className="competitor-meta">
                                      <span className="competitor-name">{competitor.name}</span>
                                    </span>
                                  </label>
                                ))}
                              </div>
                            </section>
                          ))}
                        </div>
                        {!hasCompetitorsInColumns && <p className="assistants-status">No competitors available right now.</p>}
                      </>
                    )}
                  </div>
                )}

                {activeConfigSpec.supportsStateScope && (
                  <div className="competitor-picker">
                    {jurisdictionLoading && <p className="assistants-status">Loading jurisdictions...</p>}
                    {!jurisdictionLoading && jurisdictionError && <p className="assistants-status error">{jurisdictionError}</p>}
                    {!jurisdictionLoading && !jurisdictionError && (
                      <section className="competitor-column state-jurisdiction-column">
                        <div className="competitor-group-heading">Jurisdiction</div>
                        <div className="state-grid" role="radiogroup" aria-label="State jurisdiction selection">
                          {jurisdictionOptions.map((option) => (
                            <label className={`state-card ${isJurisdictionSelected(option.scopeKey) ? 'selected' : ''}`} key={option.scopeKey}>
                              <input
                                type="radio"
                                name="selected-jurisdiction-single"
                                value={option.scopeKey}
                                checked={isJurisdictionSelected(option.scopeKey)}
                                onChange={(event) => setSelectedJurisdictionScopeKey(event.target.checked ? option.scopeKey : '')}
                              />
                              <span className="state-card-meta">
                                <span className="state-card-name">{option.jurisdictionName}</span>
                                <span className="state-card-abbrev">{getJurisdictionAbbreviation(option.scopeKey)}</span>
                              </span>
                            </label>
                          ))}
                        </div>
                      </section>
                    )}
                    {!jurisdictionLoading && !jurisdictionError && !jurisdictionOptions.length && <p className="assistants-status">No jurisdictions available right now.</p>}
                  </div>
                )}

                {activeConfigSpec.supportsCustomUrl && configureMode === 'custom' && (
                  <div className="custom-url-box">
                    <label htmlFor="custom-url-input">Competitor Website URL</label>
                    <input id="custom-url-input" type="url" value={customUrl} placeholder="https://example.com" onChange={(event) => setCustomUrl(event.target.value)} />
                  </div>
                )}

                {!hasAnyConfigOption && (
                  <div className="no-config-message" role="status">
                    <img
                      className="no-config-message-illustration"
                      src={agentNotConfiguredIllustration}
                      alt=""
                      width={360}
                      height={360}
                      decoding="async"
                    />
                    <p>
                      <strong>This agent is not configured right now.</strong>
                    </p>
                  </div>
                )}

                {configureValidationError && <p className="assistants-status error">{configureValidationError}</p>}
              </div>
              <div className="p-dialog-footer">
                <button type="button" className="cancel-btn" onClick={closeConfigure}>Cancel</button>
                <button type="button" className="add-btn" disabled={isAddDisabled()} onClick={saveConfiguration}>Add</button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </Layout>
  );
};

export default Assistants;
